// File Location: src/context/AuthContext.jsx

import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebaseConfig';
import { cacheService } from '../services/cacheService';
import { getDashboardRouteByRole } from '../utils/navigationUtils';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  // Multi-role support
  const [roles, setRoles] = useState([]);
  const [activeRole, setActiveRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user data from Firestore
  const fetchUserData = async (user) => {
    try {
      
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      console.log('User document:', userDoc);
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData(data);
        const userRoles = Array.isArray(data.roles) && data.roles.length > 0 ? data.roles : (data.role ? [data.role] : []);
        setRoles(userRoles);
        // Choose activeRole: prefer existing activeRole if still valid, else first role
        const nextActive = userRoles.includes(activeRole) ? activeRole : (userRoles[0] || null);
        setActiveRole(nextActive);
        setUserRole(data.role || nextActive || null);
        return data;
      } else {
        console.log('No user document found');
        return null;
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      return null;
    }
  };

  const logout = async () => {
    try {
      // Clear all cached data using the cache service
      await cacheService.clearAllCache();
      
      // Sign out from Firebase (this clears Firebase auth cache)
      await signOut(auth);
      
      // Clear React state
      setCurrentUser(null);
      setUserRole(null);
  setRoles([]);
  setActiveRole(null);
      setUserData(null);
      
      // Redirect to login page instead of reloading
      window.location.href = '/login';
      
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails, clear local state and redirect to login
      setCurrentUser(null);
      setUserRole(null);
      setUserData(null);
      
      // Try to clear cache even if logout fails
      try {
        await cacheService.clearAllCache();
      } catch (cacheError) {
        console.error('Cache clearing failed during error recovery:', cacheError);
      }
      
      // Always redirect to login page
      window.location.href = '/login';
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user);
      if (user) {
        setCurrentUser(user);
        await fetchUserData(user);
      } else {
        setCurrentUser(null);
        setUserRole(null);
  setRoles([]);
  setActiveRole(null);
        setUserData(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Effect to handle navigation history after authentication
  useEffect(() => {
    if (!loading && currentUser && (activeRole || userRole)) {
      // Replace current entry in history if user is on login or home page
      const currentPath = window.location.pathname;
      if (currentPath === '/' || currentPath === '/login') {
        const dashboardRoute = getDashboardRouteByRole(activeRole || userRole);
        window.history.replaceState(null, '', dashboardRoute);
      }
    }
  }, [currentUser, userRole, activeRole, loading]);

  const value = {
    currentUser,
    userRole,
    roles,
    activeRole,
    setActiveRole,
    userData,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// For convenience, also export the logout function so it can be used in components
export const useLogout = () => {
  const { logout } = useAuth();
  return logout;
};